<script setup lang="ts">
import { computed, defineComponent, onMounted, ref, watch } from "vue";
import { useConfigurationsStore } from "../../stores";

interface IsicItem {
  id: string
  code: string
  description: string
  children?: IsicItem[]
  file?: boolean
}

const store = useConfigurationsStore();
const items = ref<IsicItem[]>([])

const getIsicFetchChildren = computed(() => store.isicChildren);

onMounted(() => {
  store.fetchLevelOneIsic();
})

watch(getIsicFetchChildren, (data) => {
  if (data) {
    items.value = data
  }
})

// Tree Item Component
const TreeItem = defineComponent({
  name: 'TreeItem',
  props: {
    item: {
      type: Object as () => IsicItem,
      required: true
    },
    level: {
      type: Number,
      required: true
    }
  },
  setup(props) {
    const isOpen = ref(false)

    const toggleOpen = () => {
      if (props.item.children && props.item.children.length > 0) {
        isOpen.value = !isOpen.value
      }
    }

    return {
      isOpen,
      toggleOpen
    }
  },
  template: `
    <div class="tree-item">
      <div
        class="flex items-center py-2 px-2 hover:bg-primary-50 rounded cursor-pointer transition-colors duration-150"
        :style="{ paddingLeft: (level * 24 + 8) + 'px' }"
        @click="toggleOpen"
      >
        <i
          v-if="item.children && item.children.length > 0"
          :class="isOpen ? 'fas fa-chevron-down' : 'fas fa-chevron-right'"
          class="text-gray-500 text-xs w-4 mr-2"
        ></i>
        <span v-else class="w-4 mr-2"></span>

        <i
          :class="item.file ? 'fa fa-file text-primary-500' : 'fa fa-folder text-warning-500'"
          class="mr-2"
        ></i>

        <span
          class="text-sm text-gray-700 hover:text-primary-700"
          :title="item.description"
        >
          {{ item.description }}
        </span>
      </div>

      <div v-if="isOpen && item.children && item.children.length > 0">
        <TreeItem
          v-for="child in item.children"
          :key="child.id"
          :item="child"
          :level="level + 1"
        />
      </div>
    </div>
  `
})
</script>

<template>
  <div class="bg-white rounded border border-gray-200 p-4">
    <TreeItem
      v-for="item in items"
      :key="item.id"
      :item="item"
      :level="0"
    />
  </div>
</template>

<style scoped>
.tree-item {
  user-select: none;
}
</style>
